/**
 * An object's `dimensions` after a resize.
 *
 * `dimensions` carries more than the box: a sink's `cutoutDepth`, a corner
 * cabinet's `isUpper` / `cornerBox` / legs, a surround's
 * `sidePanelThickness`, a hood's `chimneyHeight`. Every resize gesture and
 * size editor produces just `{x, y, z}`, and writing that in place of the
 * stored field dropped the rest — a resized corner upper came back as a base
 * unit (#2886).
 *
 * So a resize KEEPS every key and is responsible for the ones that depend on
 * the size, which this function updates:
 *
 *   width, height, depth   a sink's exact copies of x, y, z
 *                          (`shared-lib/sink-types` `buildSinkDimensions`);
 *                          readers prefer them, so a stale copy is worse than
 *                          a missing one.
 *   alongA, alongB         a corner cabinet's legs, equal to x and z by
 *                          construction (`buildCornerCabinet`).
 *   chimneyHeight          a hood's chimney, which runs from the hood's top to
 *                          the ceiling: the ceiling stays where it is.
 *
 * A copy is updated only where the stored dimensions already carry it, and a
 * key the caller passes explicitly is taken as given.
 *
 * @param {Object} previous - the stored `dimensions` (may be missing)
 * @param {Object} next - the new size; usually `{x, y, z}`
 * @param {Object} [centers] - `{ previousCenterY, nextCenterY }`, the
 *   object's centre height before and after; read only for `chimneyHeight`,
 *   and an omitted one means the centre did not move.
 * @returns {Object} the dimensions to store
 */
const ALIASES = [
  ['width', 'x'],
  ['height', 'y'],
  ['depth', 'z'],
  ['alongA', 'x'],
  ['alongB', 'z'],
];

const isNum = (v) => typeof v === 'number' && Number.isFinite(v);

function resizeDimensions(previous, next, centers = {}) {
  const prev = previous || {};
  const out = { ...prev, ...next };
  const given = (key) => Object.prototype.hasOwnProperty.call(next || {}, key);

  for (const [alias, axis] of ALIASES) {
    if (isNum(prev[alias]) && !given(alias) && isNum(out[axis])) {
      out[alias] = out[axis];
    }
  }

  if (
    isNum(prev.chimneyHeight) &&
    !given('chimneyHeight') &&
    isNum(prev.y) &&
    isNum(out.y)
  ) {
    const prevCenterY = isNum(centers.previousCenterY)
      ? centers.previousCenterY
      : 0;
    const nextCenterY = isNum(centers.nextCenterY)
      ? centers.nextCenterY
      : prevCenterY;
    const ceilingY = prevCenterY + prev.y / 2 + prev.chimneyHeight;
    out.chimneyHeight = Math.max(0, ceilingY - (nextCenterY + out.y / 2));
  }

  return out;
}

module.exports = { resizeDimensions };
